<script setup lang="ts">
import { Youtube } from 'lucide-vue-next'
import Logo from '../brand/Logo.vue'
import { appLoginUrl } from '../../config'

interface FooterLink {
  label: string
  href: string
  external?: boolean
}

const productLinks: FooterLink[] = [
  { label: 'How it works', href: '/how-it-works' },
  { label: 'Use cases', href: '/use-cases' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Referrals', href: '/referrals' },
  { label: "What's New", href: '/whats-new' },
  { label: 'Web App', href: appLoginUrl(), external: true },
  { label: 'Download', href: '/download' },
  { label: 'Legal', href: '/legal' },
]

// The channel is where the films live. Videos made here get published there
// rather than only self-hosted, so it belongs in the community column as a
// destination, not just as an icon.
const YOUTUBE_CHANNEL = 'https://www.youtube.com/@OpenMuseSocial'

const communityLinks = [
  { label: 'YouTube', href: YOUTUBE_CHANNEL, external: true },
  { label: 'Contact', href: 'mailto:contact@open-muse.com', external: true },
]

const socialLinks = [
  { icon: Youtube, href: YOUTUBE_CHANNEL, label: 'YouTube' },
]
</script>

<template>
  <footer class="border-t border-surface-800 bg-surface-950">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
        <div class="col-span-2">
          <RouterLink to="/" class="mb-3 flex min-h-11 w-fit items-center gap-2.5 rounded-control" aria-label="OpenMuse home">
            <Logo class="w-7 h-7" />
            <span class="font-display text-base font-bold text-surface-50">OpenMuse</span>
          </RouterLink>
          <p class="mb-5 max-w-xs text-sm leading-relaxed text-surface-400">
            AI video song creator for turning your photos and videos into music videos with generated lyrics and music.
          </p>
          <div class="flex gap-2">
            <a
              v-for="social in socialLinks"
              :key="social.label"
              :href="social.href"
              target="_blank"
              rel="noopener"
              class="flex size-11 items-center justify-center rounded-control bg-surface-900 text-surface-400 transition-colors hover:bg-surface-800 hover:text-primary-300"
              :aria-label="social.label"
            >
              <component :is="social.icon" class="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-400">Product</h4>
          <ul class="space-y-2">
            <li v-for="link in productLinks" :key="link.label">
              <a
                v-if="link.external"
                :href="link.href"
                class="inline-flex min-h-11 items-center rounded-sm text-sm text-surface-400 transition-colors hover:text-primary-300"
              >
                {{ link.label }}
              </a>
              <RouterLink v-else :to="link.href" class="inline-flex min-h-11 items-center rounded-sm text-sm text-surface-400 transition-colors hover:text-primary-300">
                {{ link.label }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <div>
          <h4 class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-400">Community</h4>
          <ul class="space-y-2">
            <li v-for="link in communityLinks" :key="link.label">
              <a
                :href="link.href"
                :target="link.external ? '_blank' : undefined"
                :rel="link.external ? 'noopener' : undefined"
                class="inline-flex min-h-11 items-center rounded-sm text-sm text-surface-400 transition-colors hover:text-primary-300"
              >
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-10 border-t border-surface-800 pt-6 text-center">
        <p class="text-xs text-surface-400">
          &copy; {{ new Date().getFullYear() }} OpenMuse. Built for creators.
        </p>
      </div>
    </div>
  </footer>
</template>
